import { defineStore } from "pinia";
import { ref, registerRuntimeCompiler } from "vue";
import SignalRService from "@/core/services/SignalRService";

type ConnectionState =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting";

export const useSignalRStore = defineStore("signalR", () => {
  const state = ref<ConnectionState>("disconnected");
  const lastError = ref<string | null>(null);
  const connectionId = ref<string | undefined>(undefined);

  let signalR: SignalRService | null = null;

  /**
   * Pending event handlers waiting to be registered
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pendingEventHandlers = new Map<(...args: any[]) => void, string>();

  function init() {
    // If SignalRService instance exists, do not reinitialize
    if (signalR) return;
    signalR = new SignalRService();

    signalR.registerConnectionCallbacks(
      /** onClose */
      (error) => {
        lastError.value = error?.message ?? null;
        state.value = "disconnected";
        connectionId.value = undefined;
        console.log("SingalR onClose()", error);
      },
      /** onReconnecting */
      (error) => {
        lastError.value = error?.message ?? null;
        state.value = "reconnecting";
        console.log("SignalR onReconnecting()", error);
      },
      /** onReconnected */
      (_connectionId) => {
        lastError.value = null;
        state.value = "connected";
        connectionId.value = _connectionId;
        console.log("SignalR onReconnected()", _connectionId);
        flushPendingHandlers();
      }
    );
  }

  /**
   * Connect to SignalR Hub
   */
  async function connect() {
    if (!signalR) throw new Error("SignalR not initialized");
    state.value = "connecting";
    lastError.value = null;
    try {
      await signalR.start();
      state.value = "connected";
      flushPendingHandlers();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      state.value = "disconnected";
      lastError.value = e?.message ?? String(e);
      throw e;
    }
  }

  /**
   * Disconnect from SignalR Hub
   */
  async function disconnect() {
    if (!signalR) return;
    await signalR.stop();
    state.value = "disconnected";
  }

  /**
   * Send a message to SignalRHub
   * @param methodName
   * @param args
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function sendMessage(methodName: string, ...args: any[]) {
    if (!signalR) throw new Error("SignalR not initialized");
    await signalR.sendMessage(methodName, ...args);
  }

  /**
   * Invoke a method on the SignalR Hub
   * @param methodName
   * @param args
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function invokeMethod<T = any>(methodName: string, ...args: any[]) {
    if (!signalR) throw new Error("SignalR not initialized");
    if (state.value != "connected") {
      return;
    }
    await signalR.invokeMethod<T>(methodName, ...args);
  }

  /**
   * Register a message handler for a specific SignalR event
   * @param methodName The name of the SignalR event
   * @param callback The callback function to handle the event
   */
  async function registerMessageHandler(
    methodName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (...args: any[]) => void
  ) {
    if (!signalR) throw new Error("SignalR not initialized");
    if (state.value === "connected") {
      signalR.registerMessageHandler(methodName, callback);
      return;
    }
    // Store in pending handlers
    pendingEventHandlers.set(callback, methodName);
  }

  /**
   * Unregister a message handler for a specific SignalR event
   * @param methodName The name of the SignalR event
   * @param callback The callback function to handle the event
   * @returns
   */
  async function unregisterMessageHandler(
    methodName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (...args: any[]) => void
  ) {
    if (!signalR) throw new Error("SignalR not initialized");
    if (state.value === "connected") {
      signalR.unregisterMessageHandler(methodName, callback);
      return;
    }
    // Remove from pending handlers
    pendingEventHandlers.delete(callback);
  }

  async function flushPendingHandlers() {
    if (!signalR) throw new Error("SignalR not initialized");
    console.log("Flushing pending SignalR handlers", pendingEventHandlers.size);
    // Create copy, and clear map
    const _pendingEventHandlers = new Map(pendingEventHandlers);
    pendingEventHandlers.clear();
    for (const [callback, methodName] of _pendingEventHandlers) {
      registerMessageHandler(methodName, callback);
    }
  }

  return {
    //state
    state,
    lastError,
    connectionId,
    //actions
    init,
    connect,
    disconnect,
    sendMessage,
    invokeMethod,
    registerMessageHandler,
    unregisterMessageHandler,
  };
});
